import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';

// components
import Popover from '../../../juristec-ui/core/Popover';
import IconButton from '../../../juristec-ui/core/IconButton';
import List from '../../../juristec-ui/core/List';
import ListItem from '../../../juristec-ui/core/ListItem';

// icons
import {
  Dashboard,
  Download,
  MoreVert,
  Trash,
  LegalOne,
  Share,
  TransferUser,
  Visibility,
  RemoveFile,
  Rename,
  Key,
  VersionMerge,
  VersionMergeDouble,
  Edit,
} from '../../../juristec-ui/icons';

// styles
import {
  OptionsContainer,
} from './styled/FileOption.styled';

const FileOptions = ({
  user,
  item,
  viewFile,
  downloadFile,
  deleteFile,
  showFileDashboards,
  convertFileToMail,
  convertFileApiGed,
  userCompany,
  shareFile,
  changeDatabasePassword,
  transferFile,
  selfRemove,
  columnAlias,
  size = 'medium',
  shape = 'rounded',
  createFileProvision = () => {},
  listFileProvisions = () => {},
  changeDatabaseQuery = () => {},
}) => {
  const [openPopover, setOpenPopover] = useState(false);
  const theme = useTheme();

  const handleViewFile = () => viewFile(item);
  const handleDownloadFile = () => downloadFile(item);
  const handleConvertFileToMail = () => convertFileToMail(item);
  const handleConvertFileApiGed = () => convertFileApiGed(item);
  const handleColumnAlias = () => columnAlias(item);
  const handleDatabasePassword = () => changeDatabasePassword(item);
  const handleViewQuery = () => changeDatabaseQuery(item);
  const handleShareFile = () => shareFile(item);
  const handleTransferFile = () => transferFile(item);
  const handleCreateProvision = () => createFileProvision(item);
  const handleSelfRemove = () => selfRemove(item);
  const handleShowFileDashboards = () => showFileDashboards(item);
  const handleDeleteFile = () => deleteFile(item);
  const handleProvisionList = () => listFileProvisions(item);

  const checkUserDependencyPermission = () => {
    if (item?.owner === user.id) return true;
    const sharedIds = [...(user.groups ?? []), user.id];
    for (let i = 0; i < sharedIds.length; i += 1) {
      if (
        item?.shared_write?.includes(sharedIds[i])
        || item?.shared_read?.includes(sharedIds[i])
      ) return true;
    }
    return false;
  };

  return (
    <Popover
      style={{
        borderRadius: '8px',
        backgroundColor: theme.popoverBackground,
        overflow: 'hidden',
      }}
      offset={[0, 0]}
      open={openPopover}
      stayOpened={false}
      closePopover={() => setOpenPopover(false)}
    >
      <Popover.Action>
        <IconButton
          size={size}
          shape={shape}
          style={{ padding: '1px' }}
          onClick={() => setOpenPopover(!openPopover)}
        >
          <MoreVert />
        </IconButton>
      </Popover.Action>
      <Popover.Content>
        <OptionsContainer>
          <List>
            <ListItem onClick={handleViewFile} noOutline>
              <Visibility />
              Visualizar
            </ListItem>
            {(checkUserDependencyPermission()) && (
              <ListItem onClick={handleDownloadFile} noOutline>
                <Download />
                Download
              </ListItem>
            )}
            { item.origin !== 'LegalOne'
              && item.origin !== 'LegalOneExtractor'
              && item.origin !== 'L1ReportsApi'
              && item.origin !== 'Mail'
              && process.env.REACT_APP_FIREBASE_PROJECT_LABEL !== 'benner-metrics'
              && (userCompany.l1reportActive || userCompany.supportPermissionReports)
              && user.id === item.owner
              && (
                <>
                  <ListItem
                    noOutline
                    onClick={handleConvertFileToMail}
                  >
                    <LegalOne color={theme.grey} />
                    Criar Relatório (E-mail)
                  </ListItem>
                  { userCompany.apiGedCredentials && (
                    <ListItem
                      noOutline
                      onClick={handleConvertFileApiGed}
                    >
                      <LegalOne color={theme.grey} />
                      Criar Relatório (API GED)
                    </ListItem>
                  )}
                </>
              )}
            {/* Removido opção de apelidar colunas */}
            {/* {user.id === item.owner
            && (item.origin === 'Benner' || item.connector === 'LegalOneDataExtractor')
            && (
              <ListItem onClick={handleColumnAlias} noOutline>
                <Rename />
                Apelidar Colunas
              </ListItem>
            )} */}
            {user.id === item.owner
            && (item.origin === 'Benner' || item.connector === 'sql')
            && (
              <ListItem onClick={handleDatabasePassword} noOutline>
                <Key />
                Alterar Credenciais
              </ListItem>
            )}
            {user.id === item.owner
              && item.connector === 'sql'
              && (
                <ListItem onClick={handleViewQuery} noOutline>
                  <Edit />
                  Alterar consulta
                </ListItem>
              )}
            {user.id === item.owner && (
              <>
                <ListItem onClick={handleShareFile} noOutline>
                  <Share />
                  Compartilhar
                </ListItem>
                {item.origin !== 'Snapshot' && (
                  <ListItem onClick={handleTransferFile} noOutline>
                    <TransferUser />
                    Transferir
                  </ListItem>
                )}
                <ListItem onClick={handleCreateProvision} noOutline>
                  <VersionMerge />
                  Criar Histórico de Base
                </ListItem>
              </>
            )}
            {item.origin === 'Snapshot' && (
              <ListItem onClick={handleProvisionList} noOutline>
                <VersionMergeDouble />
                Listar Histórico
              </ListItem>
            )}
            {(item?.shared_read.includes(user.id)
              && !item.shared_write.some((i) => user?.groups?.includes(i))
              && !item.shared_read.some((i) => user?.groups?.includes(i)))
              && (
                <ListItem onClick={handleSelfRemove} noOutline>
                  <RemoveFile />
                  Sair do compartilhamento
                </ListItem>
              )}
            <ListItem onClick={handleShowFileDashboards} noOutline>
              <Dashboard />
              Dashboards
            </ListItem>
            {userCompany.plan !== 'push'
            && item.origin !== 'Snapshot'
            && (
              user.id === item.owner && (
                <ListItem onClick={handleDeleteFile} noOutline>
                  <Trash />
                  Apagar
                </ListItem>
              )
            )}
          </List>
        </OptionsContainer>
      </Popover.Content>
    </Popover>
  );
};

FileOptions.propTypes = {
  /**
   * User Object
   */
  user: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    groups: PropTypes.arrayOf(
      PropTypes.string,
    ),
  }).isRequired,
  /**
   * User Object
   */
  item: PropTypes.shape({
    origin: PropTypes.string,
    owner: PropTypes.string,
    shared_read: PropTypes.arrayOf(
      PropTypes.string,
    ),
    shared_write: PropTypes.arrayOf(
      PropTypes.string,
    ),
    connector: PropTypes.string,
  }).isRequired,
  /**
   * Function to show the file data
   */
  viewFile: PropTypes.func.isRequired,
  /**
   * Function to download the file
   */
  downloadFile: PropTypes.func.isRequired,
  /**
   * Function to delete the file
   */
  deleteFile: PropTypes.func.isRequired,
  /**
   * Function to show dashboards related to the file
   */
  showFileDashboards: PropTypes.func.isRequired,
  /**
   * String that determines the size of the icons
   */
  size: PropTypes.string,
  /**
   * String that determines the size of the icons
   */
  shape: PropTypes.string,
  /**
   * Function to create a Legal One report email
   */
  convertFileToMail: PropTypes.func.isRequired,
  /**
   * Function to create a Legal One report API GED
   */
  convertFileApiGed: PropTypes.func.isRequired,
  /**
   * Function to change database password (used for Benner and SQL files)
   */
  changeDatabasePassword: PropTypes.func.isRequired,
  /**
   * Function to share file from one user to others
   */
  shareFile: PropTypes.func.isRequired,
  /**
   * Function to transfer the ownership from one user to another
   */
  transferFile: PropTypes.func.isRequired,
  /**
   * Function to create (or concatenate) a new version of the current file
   */
  createFileProvision: PropTypes.func,
  /**
   * Function to list all the provisions created
   */
  listFileProvisions: PropTypes.func,
  /**
   * Function to self remove a user from a shared file
   */
  selfRemove: PropTypes.func.isRequired,
  /**
   * Function to rename columns from a file
   */
  columnAlias: PropTypes.func.isRequired,
  /**
   * String that determines the size of the icons
   */
  userCompany: PropTypes.shape({
    plan: PropTypes.string,
    l1reportActive: PropTypes.bool,
    apiGedCredentials: PropTypes.bool,
    supportPermissionReports: PropTypes.bool,
  }).isRequired,
  /**
   * Function to change database query
   */
  changeDatabaseQuery: PropTypes.func,
};

export default FileOptions;
